import { useEffect, useState } from 'react';

import { FaceToFaceLog } from '@/constants/f2f';
import useAppDownloadCujModal from '@/hooks/Cuj/useAppDownloadCujModal';
import useCustomRouter from '@/hooks/useCustomRouter';
import { CustomPageProps } from '@/types/app';
import { MedicalCategorySortType } from '@/types/bff/gql-schema';
import { ROUTES } from '@/types/common';
import { HospitalSearchParamsQueries, MedicalCategoryVo } from '@/types/hospital';
import { faceTofaceLogger } from '@/utils/logger';

import { EPixelEventName } from '@/constants/pixel';
import useModal from '@/hooks/useModal';
import { MODAL_BUTTON_TYPE, MODAL_TYPE } from '@/types/modal';
import Pixel from '@/utils/pixel/pixel';
import { validateNonSpecialPattern } from '@/utils/validate';
import useFaceToFaceTreatmentKeywords from './useFaceToFaceTreatmentKeywords';
import useMedicalCategories from './useMedicalCategories';

const MOBILE_CATEGORY_COUNT = 14;
const WEB_CATEGORY_COUNT = 15;

const useHospitalHomeViewModel = ({ client }: CustomPageProps) => {
  const { moveToPage, router, pageName } = useCustomRouter();
  const { showModal, closeModal } = useModal();
  const departmentGroupId = router.query.departmentGroupId as string | undefined;
  const { data: treatments = [] } = useFaceToFaceTreatmentKeywords();
  const { data: categories = [] } = useMedicalCategories({
    sortType: MedicalCategorySortType.Recommendation,
  });
  const homeDiagnosisCategories = client.isMobile
    ? categories.slice(0, MOBILE_CATEGORY_COUNT)
    : categories.slice(0, WEB_CATEGORY_COUNT);
  const { data: allCategories = [] } = useMedicalCategories({
    sortType: MedicalCategorySortType.Alphabet,
    enabled: !client.isMobile,
  });
  const [isCategoriesModalOpen, setIsCategoriesModalOpen] = useState(false);
  const [isTreatmentsModalOpen, setIsTreatmentsModalOpen] = useState(false);

  const { setIsCujModalTrackingInfo } = useAppDownloadCujModal();

  const navigateToNearSearchHospitalsFromKeyword = (keyword: string, type: 'category' | 'treatment') => {
    const isCategoryType = type === 'category';

    faceTofaceLogger({
      eventName: isCategoryType ? FaceToFaceLog.click_category : FaceToFaceLog.click_category_treatment,
      pageName: pageName,
      payload: {
        keyword,
      },
    });

    moveToPage({
      mobileScreenName: ROUTES.HOSPITAL.SEARCH.appScreenName,
      webLink: `${ROUTES.HOSPITAL.SEARCH.path}?${HospitalSearchParamsQueries.Keyword}=${keyword}&${HospitalSearchParamsQueries.Mode}=NEAR&${HospitalSearchParamsQueries.SortType}=DISTANCE`,
      params: {
        keyword,
        mode: 'NEAR',
        sortType: 'DISTANCE',
      },
    });
  };

  const navigateToSearchHospitalsFromKeyword = (keyword: string) => {
    if (!validateNonSpecialPattern(keyword)) {
      showModal({
        type: MODAL_TYPE.ALERT,
        props: {
          title: '특수문자를 지원하지 않아요.',
          message: '다른 검색어를 입력해주세요.',
          buttons: [{ type: MODAL_BUTTON_TYPE.BOLD, label: '확인', onClick: closeModal }],
        },
      });
      return;
    }
    faceTofaceLogger({
      eventName: FaceToFaceLog.click_search_keyword,
      pageName: pageName,
      payload: {
        keyword,
      },
    });

    moveToPage({
      mobileScreenName: ROUTES.HOSPITAL.SEARCH.appScreenName,
      webLink: `${ROUTES.HOSPITAL.SEARCH.path}?${HospitalSearchParamsQueries.Keyword}=${keyword}`,
      params: {
        keyword,
      },
    });
  };

  const openMedicalCategories = () => {
    if (client.isMobile) {
      moveToPage({
        mobileScreenName: ROUTES.HOSPITAL.CATEGORIES.appScreenName,
        webLink: ROUTES.HOSPITAL.CATEGORIES.path,
      });
      return;
    }
    setIsCategoriesModalOpen(true);
  };

  const closeMedicalCategories = () => {
    setIsCategoriesModalOpen(false);
  };

  const openHospitalTreatments = () => {
    if (client.isMobile) {
      moveToPage({
        mobileScreenName: ROUTES.HOSPITAL.TREATMENTS.appScreenName,
        webLink: ROUTES.HOSPITAL.TREATMENTS.path,
      });
      return;
    }
    setIsTreatmentsModalOpen(true);
  };

  const closeHospitalTreatments = () => {
    setIsTreatmentsModalOpen(false);
  };

  const clickCategory = (category: MedicalCategoryVo) => {
    navigateToNearSearchHospitalsFromKeyword(category.name, 'category');
  };

  useEffect(() => {
    Pixel.trackCustom(EPixelEventName.PageIn, {});
    setIsCujModalTrackingInfo({
      campaignName: 'web_hospitals',
      trackContent: 'web_mid_mdl_main',
    });
  }, []);

  return {
    categories: homeDiagnosisCategories,
    treatments,
    navigateToSearchHospitalsFromKeyword,
    navigateToNearSearchHospitalsFromKeyword,
    isCategoriesModalOpen,
    isTreatmentsModalOpen,
    openMedicalCategories,
    closeMedicalCategories,
    openHospitalTreatments,
    closeHospitalTreatments,
    allCategories,
    clickCategory,
    departmentGroupId,
  };
};

export default useHospitalHomeViewModel;
